import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { restaurantApi } from '../../../../api/restaurant';
import { TTMenu } from '../../../../interfaces/menus';
import {
  RestaurantAddress,
  TTRestaurantProfile,
} from '../../../../interfaces/restaurant';
import { RootState } from '../../../../rootReducer';

type Props = {};

// Helper function to check if content is HTML or plain text
const isHtmlContent = (content: string): boolean => {
  // Check for common HTML tags that would indicate rich text content
  const htmlTagRegex = /<(p|h[1-6]|ul|ol|li|strong|em|u|s|a|img|br)\b[^>]*>/i;
  return htmlTagRegex.test(content);
};

// Helper function to convert plain text to HTML for rendering
const convertPlainTextToHtml = (plainText: string): string => {
  if (!plainText || plainText.trim() === '') {
    return '';
  }
  
  // Split by double line breaks to create paragraphs
  const paragraphs = plainText.split(/\n\s*\n/);
  
  return paragraphs
    .map(paragraph => {
      if (paragraph.trim() === '') return '';
      
      // Convert single line breaks to <br> tags within paragraphs
      const formattedParagraph = paragraph
        .trim()
        .replace(/\n/g, '<br>');
      
      return `<p class="text-on-surface-variant">${formattedParagraph}</p>`;
    })
    .filter(p => p !== '')
    .join('');
};

// Helper function to add text classes to HTML elements
const addTextClassesToHtml = (html: string): string => {
  if (!html || html.trim() === '') {
    return '';
  }
  
  // Add text-on-surface-variant class to various text elements
  let processedHtml = html;
  
  // Process different HTML elements and add the text class
  const textElements = [
    'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'small', 'del', 'ins', 'sub', 'sup',
    'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'span', 'div'
  ];
  
  textElements.forEach(element => {
    // Handle elements that already have classes
    processedHtml = processedHtml.replace(
      new RegExp(`<${element}\\s+class="([^"]*)"`, 'gi'),
      `<${element} class="$1 text-on-surface-variant"`
    );
    
    // Handle elements that already have other attributes but no class
    processedHtml = processedHtml.replace(
      new RegExp(`<${element}\\s+([^>]*?)(?<!class="[^"]*")>`, 'gi'),
      (match, attrs) => {
        if (attrs.includes('class=')) {
          return match; // Already handled above
        }
        return `<${element} ${attrs} class="text-on-surface-variant">`;
      }
    );
    
    // Handle elements with no attributes
    processedHtml = processedHtml.replace(
      new RegExp(`<${element}>`, 'gi'),
      `<${element} class="text-on-surface-variant">`
    );
  });
  
  return processedHtml;
};

// Helper function to prepare content for rendering
const prepareContentForRendering = (content: string): string => {
  if (!content || content.trim() === '') {
    return '';
  }
  
  let processedContent: string;
  
  // If it's already HTML, use as-is
  if (isHtmlContent(content)) {
    processedContent = content;
  } else {
    // If it's plain text, convert to HTML
    processedContent = convertPlainTextToHtml(content);
  }
  
  // Add text classes to HTML elements
  return addTextClassesToHtml(processedContent);
};

// Helper function to truncate bio content to first few lines
const truncateBio = (bioContent: string, maxLines: number = 3): string => {
  if (!bioContent || bioContent.trim() === '') {
    return '';
  }
  
  // If it's plain text, split by line breaks and take first few lines
  if (!isHtmlContent(bioContent)) {
    const lines = bioContent.split('\n').slice(0, maxLines);
    return lines.join('\n');
  }
  
  // For HTML content, we'll use a simpler approach - truncate by paragraph count
  const paragraphs = bioContent.split('</p>');
  if (paragraphs.length <= maxLines) {
    return bioContent;
  }
  
  return paragraphs.slice(0, maxLines).join('</p>') + '</p>';
};

export function RestaurantInfoCard() {
  const selectedMenu = useSelector<RootState, TTMenu | null>(
    state => state.restaurant.menu_in_view,
  );

  const primaryRestaurant = useSelector<RootState, TTRestaurantProfile | null>(
    state => state.restaurant.profile,
  );

  const [menuOwnerProfile, setMenuOwnProfile] =
    useState<TTRestaurantProfile | null>(null);
  const [primaryAddress, setPrimaryAddress] =
    useState<RestaurantAddress | null>(null);
  const [showMerchantDetails, setShowMerchantDetails] = useState(false);
  
  const fetch = async () => {
    try {
      const response = await restaurantApi.getProfile(
        selectedMenu?.restaurant_id,
      );
      const addResponse = await restaurantApi.getAddresses(
        selectedMenu?.restaurant_id,
      );
      setPrimaryAddress(
        addResponse?.data?.filter(a => a.is_primary)[0] ?? null,
      );
      setMenuOwnProfile(response.data);
    } catch (e) {}
  };

  useEffect(() => {
    if (selectedMenu !== null) {
      fetch();
    }
  }, [selectedMenu?.restaurant_id]);

  // Function to safely render bio content (both HTML and plain text)
  const renderBioContent = (bioContent: string, truncated: boolean = false) => {
    const contentToRender = truncated ? truncateBio(bioContent) : bioContent;
    const preparedContent = prepareContentForRendering(contentToRender);
    // Remove empty paragraphs that might be created
    const cleanedHTML = preparedContent.replace(/<p><\/p>/g, '');
    return { __html: cleanedHTML };
  };

  return (
    <>
      <div className="flex-col bg-surface border border-outline rounded-md py-2 px-4 mb-4  mx-4 md:mx-8 lg:mx-16 xl:mx-32">
        <div className="flex items-start justify-between">
          <div className="flex bg-surface items-start space-x-2">
            <div className="flex flex-col items-center space-y-2 flex-shrink-0">
              {menuOwnerProfile?.main_profile_image && (
                <img
                  className="w-20 h-20 md:w-28 md:h-28 rounded-md object-cover"
                  src={menuOwnerProfile?.main_profile_image}
                  alt=""
                />
              )}
              <button
                className="flex rounded-full px-2 py-1 bg-secondary-container  text-sm text-on-secondary-container"
                onClick={() => setShowMerchantDetails(!showMerchantDetails)}
              >
                {showMerchantDetails ? 'Less' : 'More'}
              </button>
            </div>
            <div className="flex-col flex-1 min-w-0">
              <div className="mx-auto flex-shrink-0 flex  text-md md:text-lg text-on-surface font-bold">
                {selectedMenu?.name}
              </div>
              {selectedMenu?.description && selectedMenu?.description?.length !== 0 && (
                <div 
                  className="mx-auto flex-shrink-0 text-sm md:text-base text-on-surface-variant prose prose-sm max-w-none mt-1"
                  dangerouslySetInnerHTML={renderBioContent(selectedMenu?.description, true)}
                />
              )}
            </div>
          </div>
        </div>
        {showMerchantDetails && (
          <div className="border-t border-gray-200 sm:p-0 mt-4">
            <dl className="divide-y divide-gray-200 space-y-4">
              <div className="pt-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <dt className="text-sm font-medium text-gray-500">Merchant</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                  <div className="font-medium">{menuOwnerProfile?.name}</div>
                </dd>
              </div>
              {menuOwnerProfile?.bio && menuOwnerProfile?.bio?.length !== 0 && (
                <div className="pt-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                  <dt className="text-sm font-medium text-gray-500">About Merchant</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    <div 
                      className="prose prose-sm max-w-none text-gray-900"
                      dangerouslySetInnerHTML={renderBioContent(menuOwnerProfile?.bio, false)}
                    />
                  </dd>
                </div>
              )}
              {primaryAddress !== null && (
                <div className="pt-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    {primaryAddress?.address_line_1}, {primaryAddress?.city},{' '}
                    {primaryAddress?.administrative_area_level_1},{' '}
                    {primaryAddress?.city}, {primaryAddress?.country}{' '}
                    {primaryAddress?.zip_postal}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>
    </>
  );
}
